'use client';

import { FormEvent } from 'react';
import { isAxiosError } from 'axios';

import { Modal } from '@wishket/design-system';

import useCollectionModalForm from './useCollectionModalForm';
import { SUBMIT_TITLE_ERROR_MESSAGE } from './CollectionModal.constants';

import { useCollectionEditMutation } from '@/entities/collection';
import { useDeviceCheck, useProfile } from '@/shared/hooks';
import {
  CollectionStatus,
  CollectionFormSnakeCase,
  Collection,
} from '@/shared/types';
import { CollectionModalForm } from '@/features/Collection';

interface CollectionEditModalProps {
  isOpen: boolean;
  collectionData?: Collection;
  onClose: () => void;
}

const CollectionEdit = ({
  collectionData,
  onClose,
}: Omit<CollectionEditModalProps, 'isOpen'>) => {
  const { profile } = useProfile();
  const { value, onChange, tagData, error, validateForm, setErrorState } =
    useCollectionModalForm({ defaultCollectionData: collectionData });

  const { mutateAsync: collectionEdit, isPending } =
    useCollectionEditMutation();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isPending) return;
    if (!profile?.yozmitProfileId || !collectionData) return;
    if (!validateForm()) return;

    const profileId = profile.yozmitProfileId;
    const isPublic = value.status === CollectionStatus.PUBLIC;
    const hashTags = isPublic ? tagData.selectedItems : [];

    const collection = {
      title: value.title,
      is_fixed: false,
      hash_tags: hashTags,
      is_public: isPublic,
      profile_id: profileId,
    } as CollectionFormSnakeCase;

    if (value.description) {
      collection.description = value.description;
    }

    try {
      await collectionEdit({ id: collectionData.id, collection });

      onClose();
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        const { data } = error.response.data.error;

        if (data.non_field_errors[0] === SUBMIT_TITLE_ERROR_MESSAGE) {
          setErrorState((prev) => ({
            ...prev,
            title: { isError: true, message: '중복된 이름입니다.' },
          }));
        }
      } else {
        console.error(
          JSON.stringify({
            'error-log': {
              message: 'collectionEdit error',
              error: JSON.stringify(error),
            },
          })
        );
      }
    }
  };

  return (
    <CollectionModalForm
      type="edit"
      onClose={onClose}
      value={value}
      onChange={onChange}
      error={error}
      tag={tagData}
      onSubmit={onSubmit}
    />
  );
};

const CollectionEditModal = ({
  isOpen,
  collectionData,
  onClose,
}: CollectionEditModalProps) => {
  const { isDesktop } = useDeviceCheck();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      variant={isDesktop ? 'default' : 'full'}
      rootId="portal"
    >
      <CollectionEdit onClose={onClose} collectionData={collectionData} />
    </Modal>
  );
};

export default CollectionEditModal;
